import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const SoftwareML: React.FC = () => {
  const softwareSolutions = [
    {
      title: "Intelligent Systems",
      description: "Advanced AI and machine learning systems for automation and optimization.",
      icon: "🤖",
      features: ["Machine learning algorithms", "Predictive analytics", "Automated decision making"]
    },
    {
      title: "Industrial Automation",
      description: "Software solutions that automate and optimize industrial processes.",
      icon: "🏭",
      features: ["Process automation", "Quality control", "Production optimization"]
    },
    {
      title: "Digital Transformation",
      description: "Comprehensive digital transformation solutions for modern businesses.",
      icon: "💻",
      features: ["Cloud solutions", "Data analytics", "Business intelligence"]
    },
    {
      title: "IoT & Smart Systems",
      description: "Internet of Things solutions for connected and intelligent systems.",
      icon: "📡",
      features: ["Sensor networks", "Real-time monitoring", "Smart controls"]
    }
  ];

  const services = [
    "Custom software development",
    "Machine learning model development",
    "Data analytics and visualization",
    "Cloud infrastructure solutions",
    "System integration services",
    "Technical consulting and support"
  ];

  const benefits = [
    {
      title: "Efficiency",
      description: "Automated systems that increase operational efficiency by up to 500%",
      icon: "⚡"
    },
    {
      title: "Innovation",
      description: "Cutting-edge technology that drives innovation and competitive advantage",
      icon: "🚀"
    },
    {
      title: "Insights",
      description: "Data-driven insights that enable better decision making",
      icon: "📊"
    },
    {
      title: "Scalability",
      description: "Scalable solutions that grow with your business needs",
      icon: "📈"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-spin-slow">
                <EngineeringIcon type="screw" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Software & Machine Learning</h1>
            <p className="text-xl mb-8">
              Cutting-edge software engineering and machine learning hub. Developing intelligent systems, 
              automation solutions, and digital transformation technologies.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Digital Innovation</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">AI & ML Solutions</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Smart Automation</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Software Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Software Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Advanced software engineering and machine learning solutions designed for efficiency, innovation, and growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {softwareSolutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-pulse">{solution.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{solution.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-indigo-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Benefits of Our Software Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our software and machine learning solutions deliver exceptional benefits for your digital transformation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors">
                <div className="text-4xl mb-4 animate-bounce-slow">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive software engineering and machine learning services to meet all your digital needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-indigo-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Transform Your Business with Technology</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience the power of cutting-edge software engineering and machine learning. 
            Let's build intelligent solutions that drive innovation and growth for Ekumfiman.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="screw" size="sm" animated={false} />
              Start Your Project
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoftwareML; 